import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { type BrandingConfig, type WhiteLabelSettings, DEFAULT_BRANDING, resolveBranding } from '../types/branding';

/**
 * Looks up white-label branding by the current hostname.
 * This is used on public-facing pages (login, registration) before the user is authenticated.
 * When a coach has a custom domain that matches the current hostname, their branding is loaded.
 * Otherwise, the default NLP OS branding is returned.
 */
export function useDomainBranding() {
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const hostname = window.location.hostname;

    // Skip lookup for the main platform domains
    const mainDomains = ['my.nlpos.app', 'app.humanpotentialos.com', 'localhost', '127.0.0.1'];
    if (mainDomains.includes(hostname)) {
      setBranding(DEFAULT_BRANDING);
      setIsLoading(false);
      return;
    }

    const lookup = async () => {
      try {
        const { data } = await (supabase as any)
          .rpc('get_public_branding_by_domain', { p_domain: hostname });

        if (cancelled) return;

        const brandingData = Array.isArray(data) ? data[0] : data;

        if (brandingData) {
          const wl: WhiteLabelSettings = {
            whitelabel_enabled: brandingData.whitelabel_enabled ?? false,
            wl_brand_name: brandingData.wl_brand_name ?? null,
            wl_logo_url: brandingData.wl_logo_url ?? null,
            wl_primary_color: brandingData.wl_primary_color ?? null,
            wl_secondary_color: brandingData.wl_secondary_color ?? null,
            wl_custom_domain: brandingData.wl_custom_domain ?? null,
            wl_from_email: brandingData.wl_from_email ?? null,
            wl_from_name: brandingData.wl_from_name ?? null,
          };
          setBranding(resolveBranding(wl));
        } else {
          setBranding(DEFAULT_BRANDING);
        }
      } catch {
        if (!cancelled) setBranding(DEFAULT_BRANDING);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    lookup();

    return () => {
      cancelled = true;
    };
  }, []);

  return { branding, isLoading };
}

/**
 * Looks up white-label branding by an assessment code.
 * Used on the registration page when a respondent arrives with a code —
 * we find the coach who owns that code and load their branding.
 */
export function useCodeBranding(assessmentCode: string | null) {
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!assessmentCode) {
      setBranding(DEFAULT_BRANDING);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const lookup = async () => {
      try {
        const { data: codeData } = await supabase
          .from('assessment_codes')
          .select('coach_id')
          .eq('code', assessmentCode.trim())
          .maybeSingle();

        if (cancelled) return;

        if (codeData?.coach_id) {
          const { data: coachData } = await (supabase as any)
            .rpc('get_public_branding_by_coach_id', { p_coach_id: codeData.coach_id });
          const brandingData = Array.isArray(coachData) ? coachData[0] : coachData;

          if (cancelled) return;

          if (brandingData && brandingData.whitelabel_enabled) {
            const wl: WhiteLabelSettings = {
              whitelabel_enabled: brandingData.whitelabel_enabled ?? false,
              wl_brand_name: brandingData.wl_brand_name ?? null,
              wl_logo_url: brandingData.wl_logo_url ?? null,
              wl_primary_color: brandingData.wl_primary_color ?? null,
              wl_secondary_color: brandingData.wl_secondary_color ?? null,
              wl_custom_domain: brandingData.wl_custom_domain ?? null,
              wl_from_email: brandingData.wl_from_email ?? null,
              wl_from_name: brandingData.wl_from_name ?? null,
            };
            setBranding(resolveBranding(wl));
            return;
          }
        }
        setBranding(DEFAULT_BRANDING);
      } catch {
        if (!cancelled) setBranding(DEFAULT_BRANDING);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    lookup();

    return () => {
      cancelled = true;
    };
  }, [assessmentCode]);

  return { branding, isLoading };
}
